import { useState } from 'react';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

export interface DeviceRegistrationAutoenroll {
    id: number;
    deviceId: string;
    deviceName: string;
    groupNames: string;
    orgId: number;
    counter: number;
    createdAt: string;
}

export interface CreateDeviceRegistrationRequest {
    deviceId: string;
    deviceName: string;
    groupNames?: string;
}

export function useDeviceRegistrationAutoenroll(orgId: number) {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const listDeviceRegistrations = async (): Promise<
        DeviceRegistrationAutoenroll[]
    > => {
        setIsLoading(true);
        try {
            const response = await authenticatedApi.get(
                `${orgId}/device-registration-autoenroll/`,
            );
            return response.data.data || [];
        } catch (e: any) {
            setError(e);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const createDeviceRegistrations = async (
        devices: CreateDeviceRegistrationRequest[],
    ): Promise<DeviceRegistrationAutoenroll[]> => {
        setIsLoading(true);
        try {
            const response = await authenticatedApi.post(
                `${orgId}/device-registration-autoenroll/create`,
                JSON.stringify({ devices }),
            );
            return response.data.data || [];
        } catch (e: any) {
            setError(e);
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    const createSingleDeviceRegistration = async (
        device: CreateDeviceRegistrationRequest,
    ): Promise<DeviceRegistrationAutoenroll> => {
        setIsLoading(true);
        try {
            const response = await authenticatedApi.post(
                `${orgId}/device-registration-autoenroll/create`,
                JSON.stringify({ devices: [device] }),
            );
            const result = response.data.data || [];
            return result[0];
        } catch (e: any) {
            setError(e);
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    const getDeviceRegistration = async (
        id: number,
    ): Promise<DeviceRegistrationAutoenroll | null> => {
        setIsLoading(true);
        try {
            const response = await authenticatedApi.get(
                `${orgId}/device-registration-autoenroll/${id}`,
            );
            return response.data.data || null;
        } catch (e: any) {
            setError(e);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteDeviceRegistration = async (id: number): Promise<void> => {
        setIsLoading(true);
        try {
            await authenticatedApi.delete(
                `${orgId}/device-registration-autoenroll/${id}`,
            );
        } catch (e: any) {
            setError(e);
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        listDeviceRegistrations,
        createDeviceRegistrations,
        createSingleDeviceRegistration,
        getDeviceRegistration,
        deleteDeviceRegistration,
        isLoading,
        error,
    };
}
